import { lazy, Suspense, Fragment, useRef, type ComponentType, type ReactNode } from "react";
import { useMotionOnInteraction } from "@/hooks/useMotionOnInteraction";
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
// AnimatePresence chargé UNIQUEMENT après la 1ère interaction utilisateur
// (scroll/touch/click) — sort du critical path LCP. Fallback = Fragment.
const animatePresenceLoader = () =>
  import("framer-motion").then((m) => m.AnimatePresence as unknown as ComponentType<any>);
const useDeferredAnimatePresence = (): ComponentType<{ mode?: "wait"; children?: ReactNode }> => {
  const Comp = useMotionOnInteraction("AnimatePresence", animatePresenceLoader);
  return (Comp ?? (Fragment as unknown as ComponentType<any>)) as ComponentType<{
    mode?: "wait";
    children?: ReactNode;
  }>;
};

import { CartProvider } from "./contexts/CartContext";
import AdminOnly from "./components/admin/AdminOnly";
import {
  LegacySlugRedirect,
  LegacyCatalogueRedirect,
  LegacyCategoryRedirect,
  LegacyPaginationRedirect,
  LegacyStaticRedirect,
  TrackedRedirect,
  SLUG_OVERRIDES,
} from "./components/LegacyRedirectGuard";

// Composants globaux non critiques : sortis du bundle d'entrée pour alléger
// le chemin critique LCP. Chargés en parallèle après le premier paint.
const ScrollToTop = lazy(() => import("./components/ScrollToTop"));
const CookieBanner = lazy(() => import("./components/CookieBanner"));
const Chatbot = lazy(() => import("./components/Chatbot"));
const NetworkBanner = lazy(() => import("./components/NetworkBanner"));
const FloatingCart = lazy(() => import("./components/FloatingCart"));
const CartPanel = lazy(() => import("./components/CartPanel"));

const Index = lazy(() => import("./pages/Index"));
const Catalogue = lazy(() => import("./pages/Catalogue"));
const SessionsInter = lazy(() => import("./pages/SessionsInter"));
const FormationDetail = lazy(() => import("./pages/FormationDetail"));

// Wrapper: redirect old slugs → new slugs before rendering FormationDetail
const FormationDetailWithRedirect = () => {
  const { id } = useParams<{ id: string }>();
  if (id && SLUG_OVERRIDES[id]) {
    const target = SLUG_OVERRIDES[id].startsWith("/") ? SLUG_OVERRIDES[id] : `/nos-formations/${SLUG_OVERRIDES[id]}`;
    return <Navigate to={target} replace />;
  }
  return <FormationDetail />;
};
const NaxisGroupe = lazy(() => import("./pages/NaxisGroupe"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const CGV = lazy(() => import("./pages/CGV"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const PolitiqueRGPD = lazy(() => import("./pages/PolitiqueRGPD"));
const PolitiqueCookies = lazy(() => import("./pages/PolitiqueCookies"));
const ReglementInterieur = lazy(() => import("./pages/ReglementInterieur"));
const Certifications = lazy(() => import("./pages/Certifications"));
const PartenariatsOPCO = lazy(() => import("./pages/PartenariatsOPCO"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const EspaceClient = lazy(() => import("./pages/EspaceClient"));
const SeoAudit = lazy(() => import("./pages/SeoAudit"));
const SecurityAudit = lazy(() => import("./pages/SecurityAudit"));
const ServerError = lazy(() => import("./pages/ServerError"));
const Admin = lazy(() => import("./pages/Admin"));
const FormationManagement = lazy(() => import("./pages/FormationManagement"));
const FormationExcel = lazy(() => import("./pages/FormationExcel"));
const AdminTestDevisEmail = lazy(() => import("./pages/AdminTestDevisEmail"));
const FormationLeadership = lazy(() => import("./pages/FormationLeadership"));
const FormationManagementDeProjet = lazy(() => import("./pages/FormationManagementDeProjet"));
const FormationIAEntreprise = lazy(() => import("./pages/FormationIAEntreprise"));
const BlogManagement = lazy(() => import("./pages/BlogManagement"));
const CentreVille = lazy(() => import("./pages/CentreVille"));

const queryClient = new QueryClient();

/**
 * Le swap Fragment → AnimatePresence (chargé après la 1ère interaction) ne doit
 * se produire qu'au moment d'un changement de route : à ce moment-là le sous-arbre
 * se remonte de toute façon (key={location.pathname}), donc aucun état n'est perdu.
 * Un swap en cours de route réinitialiserait l'état de la page (onglets,
 * formulaires…). On fige donc le Fragment jusqu'au 1er changement de route.
 */
const AnimatedRoutes = () => {
  const location = useLocation();
  const AnimatePresence = useDeferredAnimatePresence();
  const initialPathRef = useRef<string | null>(null);
  if (initialPathRef.current === null) initialPathRef.current = location.pathname;
  const routeChanged = location.pathname !== initialPathRef.current;
  const withPresence = routeChanged && AnimatePresence !== (Fragment as unknown as ComponentType<any>);
  const routesTree = (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /></div>} key={location.pathname}>
        <Routes location={location}>
          <Route path="/" element={<Index />} />
          <Route path="/nos-formations" element={<Catalogue />} />
          <Route path="/nos-sessions-inter" element={<SessionsInter />} />
          <Route path="/nos-formations/:id" element={<FormationDetailWithRedirect />} />
          <Route path="/qui-sommes-nous" element={<NaxisGroupe />} />
          <Route path="/nous-contacter" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/management" element={<BlogManagement />} />
          <Route path="/blog/:slug" element={<ArticleDetail />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/politique-rgpd" element={<PolitiqueRGPD />} />
          <Route path="/politique-cookies" element={<PolitiqueCookies />} />
          <Route path="/politique-de-cookies-ue" element={<PolitiqueCookies />} />
          <Route path="/reglement-interieur" element={<ReglementInterieur />} />
          <Route path="/certifications" element={<Certifications />} />
          <Route path="/partenariats-opco" element={<PartenariatsOPCO />} />
          <Route path="/mon-compte" element={<Auth />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/mon-compte/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/espace-client" element={<EspaceClient />} />
          <Route path="/seo-audit" element={<AdminOnly><SeoAudit /></AdminOnly>} />
          <Route path="/security-audit" element={<AdminOnly><SecurityAudit /></AdminOnly>} />
          <Route path="/500" element={<ServerError />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/test-devis-email" element={<AdminTestDevisEmail />} />
          <Route path="/formation-management" element={<FormationManagement />} />
          <Route path="/formation-excel" element={<FormationExcel />} />
          <Route path="/formation-leadership" element={<FormationLeadership />} />
          <Route path="/formation-management-de-projet" element={<FormationManagementDeProjet />} />
          <Route path="/formation-ia-entreprise" element={<FormationIAEntreprise />} />
          {/* ── Pages SEO locales (centres) ── */}
          <Route path="/centre-paris" element={<CentreVille />} />
          <Route path="/centre-paris-14" element={<CentreVille />} />
          <Route path="/centre-neuilly" element={<CentreVille />} />
          <Route path="/centre-bussy-saint-georges" element={<CentreVille />} />
          <Route path="/centre-fontainebleau" element={<CentreVille />} />
          {/* ── Legacy redirects ── */}
          <Route path="/course" element={<TrackedRedirect to="/nos-formations" />} />
          <Route path="/course/:slug" element={<LegacySlugRedirect />} />
          <Route path="/formation/:slug" element={<LegacySlugRedirect />} />
          <Route path="/lesson" element={<TrackedRedirect to="/nos-formations" />} />
          <Route path="/lesson/*" element={<TrackedRedirect to="/nos-formations" />} />
          <Route path="/formations" element={<TrackedRedirect to="/nos-formations" />} />
          <Route path="/formations/:slug" element={<LegacySlugRedirect />} />
          <Route path="/catalogue/*" element={<LegacyCatalogueRedirect />} />
          <Route path="/naxis-groupe" element={<LegacyStaticRedirect to="/qui-sommes-nous" />} />
          <Route path="/contact" element={<LegacyStaticRedirect to="/nous-contacter" />} />
          <Route path="/auth" element={<LegacyStaticRedirect to="/mon-compte" />} />
          <Route path="/formations-intra" element={<LegacyStaticRedirect to="/" />} />
          <Route path="/demarche-qualite" element={<LegacyStaticRedirect to="/qui-sommes-nous" />} />
          <Route path="/capskills" element={<LegacyStaticRedirect to="/" />} />
          <Route path="/accessibilite" element={<LegacyStaticRedirect to="/qui-sommes-nous" />} />
          <Route path="/profile/conseiller" element={<LegacyStaticRedirect to="/qui-sommes-nous" />} />
          <Route path="/grands-comptes" element={<LegacyStaticRedirect to="/qui-sommes-nous" />} />
          <Route path="/formations-certifiantes" element={<LegacyStaticRedirect to="/certifications" />} />
          <Route path="/lequipe-pedagogique" element={<LegacyStaticRedirect to="/qui-sommes-nous" />} />
          <Route path="/digital-learning" element={<LegacyStaticRedirect to="/" />} />
          <Route path="/centre-avon-fontainebleau" element={<LegacyStaticRedirect to="/centre-fontainebleau" />} />
          <Route path="/centre-bussy-st-georges" element={<LegacyStaticRedirect to="/centre-bussy-saint-georges" />} />
          <Route path="/inscription-2" element={<LegacyStaticRedirect to="/mon-compte" />} />
          <Route path="/centre-paris-boissiere" element={<LegacyStaticRedirect to="/centre-paris" />} />
          <Route path="/courses" element={<LegacyStaticRedirect to="/nos-formations" />} />
          <Route path="/courses/*" element={<LegacyStaticRedirect to="/nos-formations" />} />
          {/* Audit GSC mai 2026 — chemins techniques WP */}
          <Route path="/images" element={<LegacyStaticRedirect to="/" />} />
          <Route path="/images/*" element={<LegacyStaticRedirect to="/" />} />
          <Route path="/rating_over" element={<LegacyStaticRedirect to="/" />} />
          <Route path="/nos-formations/gerer-les-impayes-recouvrement-des-creances" element={<TrackedRedirect to="/" />} />
          <Route path="/nos-formations/sql-niveau-1" element={<TrackedRedirect to="/nos-formations" />} />
          <Route path="/formation/la-comptabilite-appliquee-a-la-gestion-de-pme" element={<TrackedRedirect to="/nos-formations" />} />
          <Route path="/course/sql-niveau-1" element={<TrackedRedirect to="/nos-formations" />} />
          <Route path="/nos-formations/grooming-ameliorer-et-valoriser-son-image" element={<TrackedRedirect to="/" />} />
          <Route path="/nos-formations/les-fondamentaux-du-management-de-la-qualite" element={<TrackedRedirect to="/" />} />
          <Route path="/nos-formations/formation-de-formateur-a-lautorisation-interne-de-conduite" element={<TrackedRedirect to="/nos-formations/formation-de-formateur-a-l-autorisation-interne-de-conduite" />} />
          <Route path="/nos-formations/page/:num" element={<LegacyPaginationRedirect />} />
          <Route path="/course-category/:cat" element={<LegacyCategoryRedirect />} />
          <Route path="/course-category/:cat/*" element={<LegacyCategoryRedirect />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
    </Suspense>
  );
  return withPresence ? (
    <AnimatePresence mode="wait">{routesTree}</AnimatePresence>
  ) : (
    routesTree
  );
};

// ─── Réglage du chatbot ────────────────────────────────────────────────
// false → la bulle du chatbot est masquée sur toutes les pages du site.
// true  → la bulle réapparaît.
// Le code du chatbot, ses instructions et le catalogue ne sont pas
// supprimés : changer cette seule valeur suffit tout à faire réactiver.
const CHATBOT_VISIBLE = false;

const ConditionalChatbot = () => {
  const location = useLocation();
  if (!CHATBOT_VISIBLE) return null;
  if (
    location.pathname.startsWith("/formation-management") ||
    location.pathname.startsWith("/formation-leadership")
  ) return null;
  return <Chatbot />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <CartProvider>
      <Suspense fallback={null}><Sonner /></Suspense>
      <BrowserRouter>
        <Suspense fallback={null}>
          <NetworkBanner />
          <ScrollToTop />
        </Suspense>
        <AnimatedRoutes />
        <Suspense fallback={null}>
          <FloatingCart />
          <CartPanel />
          <ConditionalChatbot />
          <CookieBanner />
        </Suspense>
      </BrowserRouter>
    </CartProvider>
  </QueryClientProvider>
);

export default App;
